import { getLocalToday } from "utils/Common/Date";
import TextArea from "../../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../../utils/Common/Constant";
import { useEffect, useRef, useState } from "react";
import Button from "../../../../Components/Common/Button/Button";
import {
  notifyOptions,
  reasonOptions,
  LOAD_HIGH,
  LOAD_MEDIUM,
} from "../constants/inspectorAssignData";
import {
  getInspectors,
  getInspectorAssignmentProjects,
} from "../../../../services/inspectorAssignService";
import Skeleton from "../../../../Components/Common/Skeleton/Skeleton";
import NoData from "./NoData";
import StepTick from "Assets/Img/StepTick";
import { capitalizeFirst } from "utils/Common/String";
import DateSelect from "Components/Common/DateSelect/DateSelect";
import ErrorMessage from "Components/Common/ErrorMessage/ErrorMessage";
import Input from "Components/Common/Input/Input";
import SearchableSelect from "Components/Common/SearchableSelect/SearchableSelect";

export type DrawerMode =
  | "assign"
  | "coverage"
  | "reassign"
  | "vacation"
  | "assignProject";
export type AssignRole = "primary" | "coverage";
export type VacationRole = "primary" | "coverage";
export interface VacationCoverageEntry {
  inspectorId: string;
  role?: VacationRole;
}

interface InspectorMeta {
  designation?: string;
  focused?: string[];
}

interface InspectorLeave {
  leaveFrom?: string;
  leaveTo?: string;
}

interface Inspector {
  id: string;
  firstName: string;
  lastName: string;
  activeProjectCount: number;
  leaves?: InspectorLeave[];
  meta?: InspectorMeta;
  companyName?: string;
}

export interface ProjectInspector {
  assignmentId: string;
  role: AssignRole;
  inspector?: {
    id?: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface Project {
  id?: string;
  name: string;
  type?: string;
  lender?: { companyName: string };
  drawNumber?: string;
  inspectionNumber?: number;
  inspectors?: ProjectInspector[];
  status?: string;
}

export interface CoverageDates {
  from: string;
  to: string;
}

export interface ProjectOption {
  id: string;
  name: string;
  meta?: string;
  type?: string;
}

export interface InspectorSummary {
  name: string;
  initials: string;
  meta?: string;
}

interface AssignmentDrawerProps {
  open: boolean;
  mode: DrawerMode;
  project?: Project | null;
  focusInspector?: string;
  inspectorSummary?: InspectorSummary;
  selectedProjectId?: string;
  onSelectProject?: (id: string) => void;
  assignedProjectIds?: string[];
  assignedInspectorIds?: string[];
  selectedInspectorId?: string | null;
  assignRole: AssignRole;
  selectedReason: string;
  coverageDates: CoverageDates;
  notes: string;
  notifyState: Record<string, boolean>;
  vacationInspectorId?: string;
  vacationProjects?: { id: string; name: string }[];
  vacationCoverage?: Record<string, VacationCoverageEntry>;
  onVacationCoverageChange?: (projectId: string, inspectorId: string) => void;
  onVacationRoleChange?: (projectId: string, role: VacationRole) => void;
  onClose: () => void;
  onSelectInspector: (id: string) => void;
  onSetAssignRole: (role: AssignRole) => void;
  onSelectReason: (reason: string) => void;
  onCoverageDateChange: (field: keyof CoverageDates, value: string) => void;
  onNotesChange: (value: string) => void;
  onToggleNotify: (id: string) => void;
  loadingLeave?: boolean;
  onSave: () => void;
  onDeleteLeave?: () => void;
  isSaving?: boolean;
}

interface AvailabilityMeta {
  label: string;
  className: string;
  dotClass: string;
  detail: string;
}

const modeCopy: Record<
  DrawerMode,
  { title: string; sub: string; submit: string }
> = {
  assignProject: {
    title: "Assign Project",
    sub: "Select a project to assign this inspector to",
    submit: "Confirm Assignment",
  },
  assign: {
    title: "Assign Inspector",
    sub: "Select a project and assign a primary inspector.",
    submit: "Confirm Assignment",
  },
  coverage: {
    title: "Add Coverage Inspector",
    sub: "Add a coverage inspector for a specific period.",
    submit: "Add Coverage",
  },
  reassign: {
    title: "Reassign Inspector",
    sub: "Replace or change the inspector assigned to this project.",
    submit: "Confirm Reassignment",
  },
  vacation: {
    title: "Set Vacation / Leave",
    sub: "Mark this inspector as unavailable and configure coverage.",
    submit: "Save & Notify",
  },
};

const parseLocalDate = (d: string) => new Date(`${d}T00:00:00`);

const getActiveLeave = (leaves: InspectorLeave[] | undefined) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return leaves?.find(
    (l) =>
      l.leaveFrom &&
      l.leaveTo &&
      today >= parseLocalDate(l.leaveFrom) &&
      today <= parseLocalDate(l.leaveTo),
  );
};

const getOverlappingLeave = (
  leaves: InspectorLeave[] | undefined,
  rangeFrom?: string,
  rangeTo?: string,
) => {
  if (!rangeFrom || !rangeTo) return undefined;
  const from = parseLocalDate(rangeFrom);
  const to = parseLocalDate(rangeTo);
  return leaves?.find(
    (l) =>
      l.leaveFrom &&
      l.leaveTo &&
      parseLocalDate(l.leaveFrom) <= to &&
      parseLocalDate(l.leaveTo) >= from,
  );
};

const getUpcomingLeave = (leaves: InspectorLeave[] | undefined) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return leaves?.find(
    (l) => l.leaveFrom && parseLocalDate(l.leaveFrom) > today,
  );
};

const fmtLeaveDate = (d: string) =>
  parseLocalDate(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const getAvailabilityMeta = (inspector: Inspector): AvailabilityMeta => {
  const activeLeave = getActiveLeave(inspector.leaves);
  if (activeLeave) {
    return {
      label: "On Leave",
      className: "vacation",
      dotClass: "amber",
      detail: `${inspector.activeProjectCount} projects · Currently on leave`,
    };
  }
  const upcomingLeave = getUpcomingLeave(inspector.leaves);
  if (upcomingLeave?.leaveFrom && upcomingLeave.leaveTo) {
    const from = fmtLeaveDate(upcomingLeave.leaveFrom);
    const to = fmtLeaveDate(upcomingLeave.leaveTo);
    const range =
      upcomingLeave.leaveFrom === upcomingLeave.leaveTo
        ? from
        : `${from}–${to}`;
    return {
      label: "Available",
      className: "available",
      dotClass: "amber",
      detail: `${inspector.activeProjectCount} projects · Upcoming vacation ${range}`,
    };
  }
  if (inspector.activeProjectCount >= LOAD_HIGH) {
    return {
      label: "Available",
      className: "available",
      dotClass: "red",
      detail: `High load — ${inspector.activeProjectCount} projects`,
    };
  }
  if (inspector.activeProjectCount >= LOAD_MEDIUM) {
    return {
      label: "Available",
      className: "available",
      dotClass: "amber",
      detail: `Medium load — ${inspector.activeProjectCount} projects`,
    };
  }
  return {
    label: "Available",
    className: "available",
    dotClass: "green",
    detail: `Low load — ${inspector.activeProjectCount} projects · Capacity available`,
  };
};

const getSubtitle = (inspector: Inspector): string => {
  const parts: string[] = [];
  if (inspector.meta?.designation) parts.push(inspector.meta.designation);
  if (inspector.meta?.focused?.length)
    parts.push(
      inspector.meta.focused
        .map((f) => f.charAt(0).toUpperCase() + f.slice(1))
        .join(" & "),
    );
  return parts.join(" · ") || inspector.companyName || "";
};

const AssignmentDrawer = ({
  open,
  mode,
  project,
  focusInspector,
  inspectorSummary,
  selectedProjectId,
  onSelectProject,
  assignedProjectIds = [],
  assignedInspectorIds = [],
  selectedInspectorId,
  assignRole,
  selectedReason,
  coverageDates,
  notes,
  notifyState,
  onClose,
  onSelectInspector,
  onSetAssignRole,
  onSelectReason,
  onCoverageDateChange,
  onNotesChange,
  onToggleNotify,
  loadingLeave,
  onSave,
  onDeleteLeave,
  isSaving,
  vacationInspectorId,
  vacationProjects = [],
  vacationCoverage = {},
  onVacationCoverageChange,
  onVacationRoleChange,
}: AssignmentDrawerProps) => {
  const [inspectors, setInspectors] = useState<Inspector[]>([]);
  const [loadingInspectors, setLoadingInspectors] = useState(false);
  const [inspectorPage, setInspectorPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const fetchingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  let label = "—";
  if (project?.status === "active" || project?.status === "draw_request")
    label = "Draw";
  else if (project?.status === "pre_construction") label = "Pre-Con";
  else if (project?.status === "completed") label = "Completed";
  const [drawerProjects, setDrawerProjects] = useState<ProjectOption[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectPage, setProjectPage] = useState(1);
  const [hasMoreProjects, setHasMoreProjects] = useState(false);
  const fetchingProjectRef = useRef(false);
  const projectSentinelRef = useRef<HTMLDivElement | null>(null);
  const [dateSubmitted, setDateSubmitted] = useState(false);
  const datesSectionRef = useRef<HTMLDivElement>(null);
  const [reassignSubmitted, setReassignSubmitted] = useState(false);
  const reassignSectionRef = useRef<HTMLDivElement>(null);

  const [coverageInspectors, setCoverageInspectors] = useState<Inspector[]>([]);
  const [loadingCoverageInspectors, setLoadingCoverageInspectors] =
    useState(false);

  const loadCoverageInspectors = useRef(async () => {
    setLoadingCoverageInspectors(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await getInspectors(1, 100);
      const data: Inspector[] = Array.isArray(res?.data?.data)
        ? res.data.data
        : [];
      setCoverageInspectors(data);
    } finally {
      setLoadingCoverageInspectors(false);
    }
  }).current;

  const loadInspectorPage = useRef(async (pageNum: number, reset = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (reset) setLoadingInspectors(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await getInspectors(pageNum, 5);
      const data: Inspector[] = Array.isArray(res?.data?.data)
        ? res.data.data
        : [];
      const totalPages: number = res?.data?.pagination?.totalPages ?? 1;
      setInspectors((prev) => (reset ? data : [...prev, ...data]));
      setHasMore(pageNum < totalPages);
      setInspectorPage(pageNum);
    } catch {
    } finally {
      fetchingRef.current = false;
      if (reset) setLoadingInspectors(false);
    }
  }).current;

  const loadProjectPage = useRef(async (pageNum: number, reset = false) => {
    if (fetchingProjectRef.current) return;
    fetchingProjectRef.current = true;
    if (reset) setLoadingProjects(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await getInspectorAssignmentProjects({
        page: pageNum,
        limit: 5,
      });
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const totalPages: number = res?.data?.pagination?.totalPages ?? 1;
      const mapped: ProjectOption[] = data.map((p: any) => ({
        id: p.id,
        name: p.name,
        meta: p.meta,
        type: p.type,
      }));
      setDrawerProjects((prev) => (reset ? mapped : [...prev, ...mapped]));
      setHasMoreProjects(pageNum < totalPages);
      setProjectPage(pageNum);
    } catch {
    } finally {
      fetchingProjectRef.current = false;
      if (reset) setLoadingProjects(false);
    }
  }).current;

  useEffect(() => {
    if (!open) return;
    setInspectors([]);
    setHasMore(false);
    setDateSubmitted(false);
    setReassignSubmitted(false);
    loadInspectorPage(1, true);
    if (mode === "assignProject" || (mode === "assign" && !project)) {
      setDrawerProjects([]);
      setHasMoreProjects(false);
      loadProjectPage(1, true);
    }
    if (mode === "vacation") {
      setCoverageInspectors([]);
      loadCoverageInspectors();
    }
  }, [open, mode, project]);

  const datesRequired =
    mode === "vacation" ||
    ((mode === "coverage" || mode === "assignProject") &&
      assignRole === "coverage");

  const datesSelected = !!coverageDates.from && !!coverageDates.to;

  const reassignIncomplete =
    mode === "vacation" &&
    vacationProjects.length > 0 &&
    vacationProjects.some((proj) => {
      const entry = vacationCoverage[proj.id];
      return !entry?.inspectorId || !entry?.role;
    });

  const handleSave = () => {
    if (datesRequired && (!coverageDates.from || !coverageDates.to)) {
      setDateSubmitted(true);
      datesSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (reassignIncomplete) {
      setReassignSubmitted(true);
      reassignSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    onSave();
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !fetchingRef.current) {
          loadInspectorPage(inspectorPage + 1, false);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, inspectorPage]);

  useEffect(() => {
    const sentinel = projectSentinelRef.current;
    if (!sentinel || !hasMoreProjects) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !fetchingProjectRef.current) {
          loadProjectPage(projectPage + 1, false);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreProjects, projectPage]);

  if (!open) return null;

  const copy = modeCopy[mode] ?? modeCopy.assign;
  const selectedStateClass =
    mode === "coverage" && assignRole === "coverage"
      ? "selectedCoverage"
      : "selectedPrimary";

  const currentAssignments =
    project?.inspectors?.map((entry) => ({
      assignmentId: entry.assignmentId,
      role: entry.role,
      name: `${entry.inspector?.firstName ?? ""} ${entry.inspector?.lastName ?? ""}`.trim(),
      initials:
        `${entry.inspector?.firstName?.[0] ?? ""}${entry.inspector?.lastName?.[0] ?? ""}`.toUpperCase(),
    })) ?? [];

  return (
    <div className="drawerOverlay" onClick={onClose} role="presentation">
      <aside className="drawer" onClick={(event) => event.stopPropagation()}>
        <header className="drawerHeader">
          <div>
            <h2>{copy.title}</h2>
            <p>
              {project
                ? `${copy.sub} · ${project.name}`
                : `${copy.sub} ${focusInspector ? `· ${focusInspector}` : ""}`}
            </p>
          </div>
          <Button type="button" className="drawerClose" onClick={onClose}>
            ×
          </Button>
        </header>

        <div className="drawerBody">
          {mode === "assignProject" && inspectorSummary ? (
            <section className="drawerSection">
              <p className="sectionLabel">Inspector</p>
              <div className="currentAssignmentCard">
                <div className="currentAssignmentRow">
                  <span className="optionAvatar">
                    {inspectorSummary.initials}
                  </span>
                  <span className="currentAssignmentInfo">
                    <strong>{inspectorSummary.name}</strong>
                    {inspectorSummary.meta && (
                      <small>{inspectorSummary.meta}</small>
                    )}
                  </span>
                </div>
              </div>
            </section>
          ) : project ? (
            <section className="drawerSection">
              <p className="sectionLabel">Project</p>
              <div className="projectSummaryCard">
                <p className="projectTitle">{project.name}</p>
                <p className="projectSub">
                  {[
                    capitalizeFirst(project.type || ""),
                    project.lender?.companyName,
                    `${label} ${project.drawNumber} · ${project.inspectionNumber} inspections`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            </section>
          ) : null}

          {project && ["reassign", "coverage"].includes(mode) ? (
            <section className="drawerSection">
              <p className="sectionLabel">Current Assignment</p>
              <div className="currentAssignmentCard">
                {currentAssignments.map((assignment) => (
                  <div
                    className="currentAssignmentRow"
                    key={assignment.assignmentId}
                  >
                    <span className="optionAvatar">{assignment.initials}</span>
                    <span className="currentAssignmentInfo">
                      <strong>{assignment.name}</strong>
                      <small>
                        {assignment.role === "primary"
                          ? "Primary inspector"
                          : "Coverage inspector"}
                      </small>
                    </span>
                    <span
                      className={`assignmentBadge ${
                        assignment.role === "primary" ? "primary" : "coverage"
                      }`.trim()}
                    >
                      {assignment.role === "primary" ? "Primary" : "Coverage"}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {mode === "reassign" ? (
            <section className="drawerSection">
              <p className="sectionLabel">Reason for Change</p>
              <div className="reasonWrap">
                {reasonOptions.map((reason) => (
                  <Button
                    type="button"
                    key={reason.id}
                    className={`reasonBtn ${selectedReason === reason.label ? "selected" : ""}`.trim()}
                    onClick={() => onSelectReason(reason.label)}
                  >
                    <span className="reasonIcon">{reason.icon}</span>
                    {reason.label}
                  </Button>
                ))}
              </div>
            </section>
          ) : null}

          {mode === "coverage" || mode === "assignProject" ? (
            <section className="drawerSection">
              <p className="sectionLabel">Assignment Type</p>
              <div className="assignRoleRow">
                <Button
                  type="button"
                  className={`roleBtn ${assignRole === "primary" ? "activePrimary" : ""}`.trim()}
                  onClick={() => onSetAssignRole("primary")}
                >
                  <div>⭐</div>
                  Primary Inspector
                  <small className="roleLable">
                    Responsible for all draws on this project
                  </small>
                </Button>
                <Button
                  type="button"
                  className={`roleBtn ${assignRole === "coverage" ? "activeCoverage" : ""}`.trim()}
                  onClick={() => onSetAssignRole("coverage")}
                >
                  <div>🔄</div>
                  Coverage Inspector
                  <small className="roleLable">
                    Covers specific draws while primary is unavailable
                  </small>
                </Button>
              </div>
            </section>
          ) : null}

          {((mode === "coverage" || mode === "assignProject") &&
            assignRole === "coverage") ||
          mode === "vacation" ? (
            <section className="drawerSection">
              <p className="sectionLabel">
                {mode === "vacation" ? "Vacation Period" : "Coverage Period"}
              </p>
              {loadingLeave ? (
                <div className="coverageDates">
                  <div className="field">
                    <Skeleton
                      width="30%"
                      height={11}
                      style={{ marginBottom: 6 }}
                    />
                    <Skeleton width="100%" height={36} borderRadius={6} />
                  </div>
                  <div className="field">
                    <Skeleton
                      width="20%"
                      height={11}
                      style={{ marginBottom: 6 }}
                    />
                    <Skeleton width="100%" height={36} borderRadius={6} />
                  </div>
                </div>
              ) : (
                <div className="coverageDates" ref={datesSectionRef}>
                  <div className="field">
                    <DateSelect
                      label="From"
                      value={coverageDates.from}
                      min={getLocalToday()}
                      onChange={(val) => onCoverageDateChange("from", val)}
                      error={dateSubmitted && !coverageDates.from}
                    />
                    <ErrorMessage
                      touched={dateSubmitted && !coverageDates.from}
                      error="Please select a start date"
                    />
                  </div>
                  <div className="field">
                    <DateSelect
                      label="To"
                      value={coverageDates.to}
                      min={
                        coverageDates.from ||
                        getLocalToday()
                      }
                      onChange={(val) => onCoverageDateChange("to", val)}
                      error={dateSubmitted && !coverageDates.to}
                    />
                    <ErrorMessage
                      touched={dateSubmitted && !coverageDates.to}
                      error="Please select an end date"
                    />
                  </div>
                </div>
              )}
            </section>
          ) : null}

          {mode === "vacation" && vacationProjects.length > 0 ? (
            <section className="drawerSection" ref={reassignSectionRef}>
              <p className="sectionLabel">
                Reassign Affected Projects <span className="req">*</span>
              </p>
              <p className="sectionHint">
                {datesSelected
                  ? "Pick a replacement inspector for each project below and choose whether they take over as Primary or provide temporary Coverage during the vacation period."
                  : "Select the vacation period above first — inspectors who are also on leave during that window can then be correctly excluded here."}
              </p>
              <div className="vacationProjectList">
                {vacationProjects.map((proj) => {
                  const entry = vacationCoverage[proj.id];
                  return (
                    <div className="vacationProjectRow" key={proj.id}>
                      <span className="vacationProjectName">{proj.name}</span>
                      <div className="vacationProjectControls">
                        <SearchableSelect<Inspector>
                          className="vacationInspectorSelect"
                          placeholder={
                            datesSelected
                              ? "Select inspector"
                              : "Select vacation dates first"
                          }
                          value={entry?.inspectorId ?? ""}
                          options={coverageInspectors.filter(
                            (i) => i.id !== vacationInspectorId,
                          )}
                          getOptionLabel={(i) => {
                            const name = `${i.firstName} ${i.lastName}`.trim();
                            const overlap = getOverlappingLeave(
                              i.leaves,
                              coverageDates.from,
                              coverageDates.to,
                            );
                            return overlap
                              ? `${name} — On leave ${fmtLeaveDate(overlap.leaveFrom!)}–${fmtLeaveDate(overlap.leaveTo!)}`
                              : name;
                          }}
                          getSelectedLabel={(i) =>
                            `${i.firstName} ${i.lastName}`.trim()
                          }
                          getOptionValue={(i) => i.id}
                          onChange={(value) =>
                            onVacationCoverageChange?.(proj.id, value)
                          }
                          isOptionDisabled={(i) =>
                            !!getOverlappingLeave(
                              i.leaves,
                              coverageDates.from,
                              coverageDates.to,
                            )
                          }
                          disabled={!datesSelected}
                          loading={loadingCoverageInspectors}
                          portal
                        />
                        <div className="vacationRoleToggle">
                          <button
                            type="button"
                            className={`vacationRoleBtn ${entry?.role === "primary" ? "selectedPrimary" : ""}`.trim()}
                            onClick={() =>
                              onVacationRoleChange?.(proj.id, "primary")
                            }
                            disabled={!datesSelected}
                          >
                            Primary
                          </button>
                          <button
                            type="button"
                            className={`vacationRoleBtn ${entry?.role === "coverage" ? "selectedCoverage" : ""}`.trim()}
                            onClick={() =>
                              onVacationRoleChange?.(proj.id, "coverage")
                            }
                            disabled={!datesSelected}
                          >
                            Coverage
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <ErrorMessage
                touched={reassignSubmitted}
                error={
                  reassignIncomplete
                    ? "Please assign a covering inspector and role to every affected project before saving."
                    : ""
                }
              />
            </section>
          ) : null}

          {mode === "assign" && !project && (
            <section className="drawerSection">
              <p className="sectionLabel">Select Project</p>
              <div className="inspectorSelectList">
                {loadingProjects ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div className="inspectorOptionSkeleton" key={i}>
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        <Skeleton width="65%" height={12} />
                        <Skeleton width="45%" height={10} />
                      </div>
                    </div>
                  ))
                ) : drawerProjects.length === 0 ? (
                  <NoData text="No active projects available" />
                ) : (
                  drawerProjects.map((proj) => {
                    const isAssigned = assignedProjectIds.includes(proj.id);
                    return (
                      <Button
                        type="button"
                        key={proj.id}
                        className={`inspectorOption ${selectedProjectId === proj.id ? "selectedPrimary" : isAssigned ? "preSelected" : ""}`.trim()}
                        onClick={() => onSelectProject?.(proj.id)}
                      >
                        <span className="optionInfo">
                          <strong>{proj.name}</strong>
                          {proj.meta && <small>{proj.meta}</small>}
                          {proj.type && (
                            <span className="optionLoad">
                              <span className="optionLoadDot green">●</span>
                              {capitalizeFirst(proj.type)}
                            </span>
                          )}
                        </span>
                        <span className="optionCheck">✓</span>
                      </Button>
                    );
                  })
                )}
                {hasMoreProjects && (
                  <div ref={projectSentinelRef} style={{ height: 1 }} />
                )}
              </div>
            </section>
          )}

          {mode !== "vacation" && (
            <section className="drawerSection">
              <p className="sectionLabel">
                {mode === "assignProject"
                  ? "Select Project"
                  : "Select Inspector"}
                <span className="req">*</span>
              </p>
              {mode === "assignProject" ? (
                <div className="inspectorSelectList">
                  {loadingProjects ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <div className="inspectorOptionSkeleton" key={i}>
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          <Skeleton width="65%" height={12} />
                          <Skeleton width="45%" height={10} />
                        </div>
                      </div>
                    ))
                  ) : drawerProjects.length === 0 ? (
                    <p
                      style={{
                        fontSize: 12,
                        color: "var(--tx3)",
                        fontFamily: "system-ui,sans-serif",
                      }}
                    >
                      No active projects available
                    </p>
                  ) : (
                    drawerProjects.map((proj) => {
                      const isAssigned = assignedProjectIds.includes(proj.id);
                      return (
                        <Button
                          type="button"
                          key={proj.id}
                          className={`inspectorOption ${selectedProjectId === proj.id ? "selectedPrimary" : isAssigned ? "selectedPrimary" : ""} ${isAssigned ? "unavailable" : ""}`.trim()}
                          onClick={() => onSelectProject?.(proj.id)}
                          disabled={isAssigned}
                        >
                          <span className="optionInfo">
                            <strong>{proj.name}</strong>
                            {proj.meta && <small>{proj.meta}</small>}
                            {proj.type && (
                              <span className="optionLoad">
                                <span className="optionLoadDot green">●</span>
                                {capitalizeFirst(proj.type)}
                              </span>
                            )}
                          </span>
                          <span className="optionCheck">
                            <StepTick />
                          </span>
                        </Button>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="inspectorSelectList">
                  {loadingInspectors
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <div className="inspectorOptionSkeleton" key={i}>
                          <Skeleton
                            width={36}
                            height={36}
                            borderRadius="50%"
                            style={{ flexShrink: 0 }}
                          />
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                            }}
                          >
                            <Skeleton width="55%" height={12} />
                            <Skeleton width="40%" height={10} />
                          </div>
                        </div>
                      ))
                    : inspectors.map((inspector) => {
                        const availabilityMeta = getAvailabilityMeta(inspector);
                        const isOnLeave = !!getActiveLeave(inspector.leaves);
                        const isSelected = selectedInspectorId === inspector.id;
                        const isAlreadyAssigned = assignedInspectorIds.includes(
                          inspector.id,
                        );
                        const isPrimaryInCoverage =
                          (mode === "coverage" || mode === "reassign") &&
                          isAlreadyAssigned;
                        const isDisabled = isOnLeave || isPrimaryInCoverage;
                        const initials =
                          `${inspector.firstName?.[0] ?? ""}${inspector.lastName?.[0] ?? ""}`.toUpperCase();
                        const fullName =
                          `${inspector.firstName} ${inspector.lastName}`.trim();

                        return (
                          <Button
                            type="button"
                            key={inspector.id}
                            className={`inspectorOption ${isSelected ? selectedStateClass : isPrimaryInCoverage ? "selectedPrimary" : isAlreadyAssigned ? "preSelected" : ""} ${isDisabled ? "unavailable" : ""}`.trim()}
                            onClick={() => onSelectInspector(inspector.id)}
                            disabled={isDisabled}
                          >
                            <span
                              className={`optionAvatar ${isOnLeave ? "leave" : "present"}`}
                            >
                              {initials}
                            </span>
                            <span className="optionInfo">
                              <strong>{fullName}</strong>
                              <small>{getSubtitle(inspector)}</small>
                              <span className="optionLoad">
                                <span
                                  className={`optionLoadDot ${availabilityMeta.dotClass}`}
                                >
                                  ●
                                </span>
                                {availabilityMeta.detail}
                              </span>
                            </span>
                            <span className="optionRight">
                              <span
                                className={`optionStatus ${isPrimaryInCoverage ? "primary" : availabilityMeta.className}`}
                              >
                                {isPrimaryInCoverage
                                  ? "Primary"
                                  : availabilityMeta.label}
                              </span>
                              <span className="optionCheck">
                                <StepTick />
                              </span>
                            </span>
                          </Button>
                        );
                      })}
                  {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
                </div>
              )}
            </section>
          )}

          {mode !== "assignProject" && mode !== "vacation" && (
            <section className="drawerSection">
              <p className="sectionLabel">Notify</p>
              <div className="notifyWrap">
                {notifyOptions.map((option) => (
                  <label className="notifyRow" key={option.id}>
                    <Input
                      type="checkbox"
                      checked={notifyState[option.id] || false}
                      onChange={() => onToggleNotify(option.id)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </section>
          )}

          <section className="drawerSection">
            <p className="sectionLabel">Notes (optional)</p>
            <TextArea
              className="fieldTextarea"
              value={notes}
              onChange={(event) => onNotesChange(event.target.value)}
              placeholder="Add context for the inspector — project location, access notes, contact on site, key flags to watch for..."
              maxChars={CHAR_LIMIT.DESCRIPTION}
            />
          </section>

          {onDeleteLeave && (
            <section className="drawerDangerZone">
              <p className="dangerZoneTitle">CANCEL VACATION</p>
              <p className="dangerZoneDesc">
                Cancelling this vacation will restore the inspector's
                availability immediately.
              </p>
              <Button className="btn btnDanger" onClick={onDeleteLeave}>
                Cancel Vacation
              </Button>
            </section>
          )}
        </div>

        <footer className="drawerFooter">
          <Button className="btn btnSecondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="btn btnPrimary fill"
            onClick={handleSave}
            disabled={isSaving || notes.length > CHAR_LIMIT.DESCRIPTION}
          >
            {isSaving ? (
              <>
                <span className="btnSpinner" />
                Saving
              </>
            ) : (
              copy.submit
            )}
          </Button>
        </footer>
      </aside>
    </div>
  );
};

export default AssignmentDrawer;
